import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { GenericModalComponent } from '@vsolv/ui-kit/modal';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Warranty } from '@wsphere/warranties/domain';
import { WarrantyCustomerPortalService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-renew-warranty-modal',
  templateUrl: './renew-warranty.modal.html',
  standalone: false,
})
export class RenewWarrantyModal {
  @ViewChild(GenericModalComponent) modal?: GenericModalComponent;

  @Output() renewed = new EventEmitter<Warranty.RenewalResult>();

  constructor(
    private warrantySvc: WarrantyCustomerPortalService,
    private toastSvc: ToastService
  ) {}

  warrantyId: string | null = null;
  userId: string | null = null;
  /** Passed to the payment-method form so "Use my home address" has something to fill in. */
  address: Warranty.Model['shippingAddress'] = null;
  userDetails: { name: string; email: string; phone: string } | null = null;

  loading = false;
  submitting = false;
  error: string | null = null;
  preview: Warranty.RenewalPreview | null = null;
  /** Set once payment succeeds (AC 4.4) — the modal switches to a success view instead of closing. */
  success: Warranty.RenewalResult | null = null;

  paymentMethodControl = new FormControl<string | null>(null);
  selectedCoverageIds = new Set<string>();

  async open(warranty: Warranty.Model) {
    this.warrantyId = warranty.id;
    this.userId = warranty.customer?.userId ?? null;
    this.address = warranty.shippingAddress;
    this.userDetails = warranty.customer
      ? { name: warranty.customer.name, email: warranty.customer.email, phone: warranty.customer.phone ?? '' }
      : null;
    this.error = null;
    this.preview = null;
    this.success = null;
    this.paymentMethodControl.setValue(null);
    this.selectedCoverageIds = new Set();
    this.modal?.open();

    this.loading = true;
    try {
      this.preview = await this.warrantySvc.previewRenewal(warranty.id);
      this.selectedCoverageIds = new Set(
        this.preview.coverageOptions.filter(option => option.selectedByDefault).map(option => option.coverageId)
      );
    } catch (err: unknown) {
      this.error = this.getErrorMessage(err, 'Unable to load renewal details. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  toggleCoverage(coverageId: string, isRemovable: boolean) {
    if (!isRemovable) return;
    if (this.selectedCoverageIds.has(coverageId)) this.selectedCoverageIds.delete(coverageId);
    else this.selectedCoverageIds.add(coverageId);
  }

  get totalAmountDue(): number {
    if (!this.preview) return 0;
    return this.preview.coverageOptions
      .filter(option => this.selectedCoverageIds.has(option.coverageId))
      .reduce((sum, option) => sum + option.price, 0);
  }

  async confirm() {
    if (!this.warrantyId || !this.paymentMethodControl.value) return;

    this.submitting = true;
    this.error = null;
    try {
      const result = await this.warrantySvc.confirmRenewal(this.warrantyId, {
        selectedCoverageIds: [...this.selectedCoverageIds],
        paymentMethodId: this.paymentMethodControl.value,
      });
      this.toastSvc.show({ type: 'success', title: 'Renewal complete', text: `New expiry: ${result.expiryDate}` });
      // Notify the parent right away (it can refresh the underlying warranty in the background)
      // but keep the modal open on a success view (AC 4.4) instead of closing immediately.
      this.renewed.emit(result);
      this.success = result;
    } catch (err: unknown) {
      // AC 4.3: preview/coverage selection stay intact so the customer can just pick another
      // payment method and retry without redoing the renewal review.
      this.error = this.getErrorMessage(err, 'Payment failed. Please try a different payment method.');
    } finally {
      this.submitting = false;
    }
  }

  /** Human-readable title for a coverage in the success summary, from the review step's options. */
  coverageTitle(coverageId: string): string {
    return this.preview?.coverageOptions.find(option => option.coverageId === coverageId)?.title ?? coverageId;
  }

  close() {
    this.modal?.close();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getErrorMessage(err: any, fallback: string): string {
    // NestJS validation errors carry `message` as a string[]; other exceptions carry it as a
    // plain string. Indexing a string with `[0]` silently returns just its first character, so
    // the array case must be checked explicitly rather than falling through to `?.[0]`.
    const message = err?.error?.message;
    if (Array.isArray(message)) return message[0] ?? fallback;
    return message ?? fallback;
  }
}
